import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import TerritoryFilters from "@/components/base/TerritoryFilters";
import { matchesTerritory, type TerritorySelection } from "@/config/territory";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { cn } from "@/utils/cn";
import type { ExportColumn } from "@/utils/export";
import { formatDate } from "@/utils/format";
import { useSuperAdmin } from "../data/SuperAdminProvider";
import {
  COMPTE_STATUT_LABELS,
  COMPTE_STATUT_OPTIONS,
  COMPTE_TYPE_CONFIG,
  getFormule,
  utilisateurRoleLabel,
  type Compte,
  type CompteType,
} from "../data/types";
import StatutBadge from "./StatutBadge";

function statutTone(statut: Compte["statut"]): string {
  switch (statut) {
    case "actif":
      return "bg-secondary-100 text-secondary-800";
    case "en_attente":
      return "bg-accent-100 text-accent-800";
    case "suspendu":
      return "bg-primary-100 text-primary-800";
    default:
      return "bg-background-100 text-foreground-500";
  }
}

function CompteIdentityCell({ compte }: { compte: Compte }) {
  const initials =
    compte.nom
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join("").toUpperCase() || "?";
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-label text-xs font-semibold",
          statutTone(compte.statut)
        )}
      >
        {initials}
      </span>
      <span className="truncate font-label font-medium text-foreground-950">{compte.nom}</span>
    </div>
  );
}

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

interface ComptesListViewProps {
  type: CompteType;
}

export default function ComptesListView({ type }: ComptesListViewProps) {
  const { comptesByType, chargement, erreur, recharger } = useSuperAdmin();
  const config = COMPTE_TYPE_CONFIG[type];
  const comptes = comptesByType(type);

  const [search, setSearch] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [territory, setTerritory] = useState<TerritorySelection>({
    region: "",
    province: "",
    commune: "",
    secteur: "",
  });

  const stats = useMemo(
    () => ({
      total: comptes.length,
      actifs: comptes.filter((compte) => compte.statut === "actif").length,
      attente: comptes.filter((compte) => compte.statut === "en_attente").length,
      suspendus: comptes.filter((compte) => compte.statut === "suspendu").length,
      desactives: comptes.filter((compte) => compte.statut === "desactive").length,
    }),
    [comptes]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = comptes.filter((compte) => {
      const matchesStatut = !statutFilter || compte.statut === statutFilter;
      const matchesSearch =
        !term ||
        [compte.nom, compte.email, compte.telephone, compte.ville, compte.responsable, compte.role]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesStatut && matchesSearch && matchesTerritory(compte, territory);
    });

    return result.sort((a, b) => {
      if (sort === "nom") return a.nom.localeCompare(b.nom, "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [comptes, search, statutFilter, sort, territory]);

  const isFiltering =
    Boolean(search.trim()) ||
    Boolean(statutFilter) ||
    Boolean(territory.region || territory.province || territory.commune || territory.secteur);

  const exportColumns: ExportColumn<Compte>[] = [
    { header: "Nom", value: (compte) => compte.nom },
    { header: "Type", value: () => config.singular },
    { header: "Email", value: (compte) => compte.email },
    { header: "Téléphone", value: (compte) => compte.telephone },
    { header: "Ville", value: (compte) => compte.ville },
    { header: "Responsable", value: (compte) => compte.responsable },
    { header: "Rôle", value: (compte) => (compte.type === "utilisateur" ? utilisateurRoleLabel(compte.role) : "") },
    { header: "Formule", value: (compte) => getFormule(compte.formuleId)?.nom ?? "" },
    { header: "Statut", value: (compte) => COMPTE_STATUT_LABELS[compte.statut] },
    { header: "Créé le", value: (compte) => formatDate(compte.createdAt) },
  ];

  const resetFilters = () => {
    setSearch("");
    setStatutFilter("");
    setTerritory({ region: "", province: "", commune: "", secteur: "" });
  };

  const columns: DataTableColumn<Compte>[] = [
    {
      key: "identite",
      header: config.singular,
      className: "min-w-[220px]",
      render: (compte) => <CompteIdentityCell compte={compte} />,
    },
    {
      key: "role",
      header: type === "utilisateur" ? "Rôle" : "Formule",
      className: "min-w-[150px]",
      render: (compte) => (
        <span className="text-foreground-700">
          {type === "utilisateur"
            ? utilisateurRoleLabel(compte.role)
            : getFormule(compte.formuleId)?.nom ?? "Aucune"}
        </span>
      ),
    },
    {
      key: "email",
      header: "Email",
      className: "min-w-[220px]",
      render: (compte) => <span className="text-foreground-700">{compte.email || "Non renseigné"}</span>,
    },
    {
      key: "telephone",
      header: "Téléphone",
      className: "min-w-[150px]",
      render: (compte) => <span className="text-foreground-700">{compte.telephone || "Non renseigné"}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: (compte) => <StatutBadge statut={compte.statut} />,
    },
    {
      key: "date",
      header: "Date",
      className: "min-w-[130px]",
      render: (compte) => <span className="text-foreground-600">{formatDate(compte.createdAt)}</span>,
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (compte) => (
        <Link
          to={`${config.basePath}/${compte.id}`}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
        >
          Voir la fiche
          <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
        </Link>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{config.title}</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{config.description}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu
            rows={filtered}
            columns={exportColumns}
            filename={`super-admin-${type}`}
            title={config.title}
            subtitle="Données actuellement affichées"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard icon={config.icon} label={config.label} value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Actifs" value={String(stats.actifs)} tone="secondary" />
        <StatCard icon="ri-time-line" label="En attente" value={String(stats.attente)} tone="accent" />
        <StatCard icon="ri-pause-circle-line" label="Suspendus" value={String(stats.suspendus)} tone="primary" />
        <StatCard icon="ri-forbid-2-line" label="Désactivés" value={String(stats.desactives)} tone="neutral" />
      </div>

      {comptes.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <TerritoryFilters
            idPrefix={`sa-territoire-${type}`}
            value={territory}
            onChange={setTerritory}
            className="mb-4"
          />
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-2xl">
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`sa-search-${type}`} className="text-xs font-label font-medium text-foreground-700">
                  Recherche
                </label>
                <Input
                  id={`sa-search-${type}`}
                  icon="ri-search-line"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nom, email, téléphone..."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor={`sa-statut-${type}`} className="text-xs font-label font-medium text-foreground-700">
                  Statut
                </label>
                <Select
                  id={`sa-statut-${type}`}
                  value={statutFilter}
                  onChange={(event) => setStatutFilter(event.target.value)}
                  options={[{ value: "", label: "Tous les statuts" }, ...COMPTE_STATUT_OPTIONS]}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5 sm:w-52">
              <label htmlFor={`sa-sort-${type}`} className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id={`sa-sort-${type}`}
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} {config.lowercase}
              {filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur {comptes.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {chargement && comptes.length === 0 && (
        <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-14">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-background-200 border-t-primary-500" />
        </div>
      )}

      {!chargement && erreur && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-5 py-4">
          <p className="flex items-center gap-2 text-sm font-label font-medium text-primary-800">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
            {erreur}
          </p>
          <button
            type="button"
            onClick={() => void recharger()}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!chargement && !erreur && comptes.length === 0 && (
        <EmptyState
          icon={config.icon}
          title={`Aucun ${config.lowercase} pour le moment`}
          description={`Les comptes sont créés par les clients depuis la page publique d'inscription, puis validés par un Super Admin dans « Inscriptions ». Aucun compte ne peut être créé depuis cet écran.`}
        />
      )}

      {comptes.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun compte ne correspond à votre recherche ou au filtre sélectionné. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(compte) => compte.id}
          minWidthClass="min-w-[900px]"
          caption={config.title}
        />
      )}

    </div>
  );
}